import React, { useRef, useState }from 'react'
import { Link, useLocation } from 'react-router-dom';

import { TabPanel, TabList as MuiTabList, TabListProps as MuiTabListProps, TabContext } from '@mui/lab';
import { styled, Box, Tab, SxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { brandGradientCss } from '../../../theme/brandGradient';


const StyledMuiTabList = styled(MuiTabList)<MuiTabListProps>(({ theme }) => ({
  '& .MuiTabs-indicator': {
    // The two RGB triples that used to sit here inline now live in `brandGradient.ts`,
    // because the selected nav icon wants the same gradient and a second copy of a
    // hardcoded colour is a colour that drifts (§36). Unchanged visually — that file
    // records these exact values as canonical rather than re-deriving them from the ramp.
    background: brandGradientCss(),
  },
  paddingLeft: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  // A tab's BOX is exactly its LABEL, and the tabs are spaced by a gap instead of by
  // padding. Two things fall out of that, both of which were broken before:
  //
  // 1. The first label sits on the page's left content datum. Measured on the
  //    certificate detail page at 1280x800, every other element on that surface —
  //    the tab ROW, the search field, the scans card — starts at x=280, while the
  //    first tab's LABEL TEXT started at 296, because each `Tab` carried
  //    `padding: 4` (16px) that inset the label inside a correctly-positioned box.
  //    A reader perceives the text, so the text is what has to line up.
  //
  // 2. The active indicator underlines the LABEL. MUI sizes the indicator to the tab
  //    ELEMENT, so with horizontal padding the underline ran 16px past the label on
  //    each side and the label looked inset within its own underline — most obvious
  //    on a middle tab like "Scans" (label 39.9px wide, underline 71.9px).
  //
  // An earlier pass zeroed only the FIRST tab's `paddingLeft`. That fixed (1) and left
  // (2) untouched everywhere, including on the first tab, whose indicator still ran
  // 16px past the label on the right. It also made the padding asymmetric, which rules
  // out the obvious alternative fix for (2) — a centered indicator span of
  // `calc(100% - 32px)` — because that only equals the label while padding is uniform.
  // Removing the padding altogether resolves both at once and needs no special case,
  // so the first-tab rule is gone rather than left in place doing nothing.
  //
  // Fixed HERE, never per page (§29's revision: per-page geometry nudges produced three
  // different header gaps across the app). Correct for a 2-tab row and a 4-tab row
  // alike, since nothing depends on tab count or label width.
  '& .MuiTabs-flexContainer': {
    // 32px reproduces the previous rhythm exactly: the old 16px right padding plus the
    // next tab's 16px left padding measured 32.0px between adjacent labels.
    gap: '32px',
  },
}))

/**
 * Restores the click target the horizontal padding used to provide.
 *
 * With `padding: 0` the target would be exactly the glyphs — 48px tall (MUI's
 * `minHeight`) but only as wide as the word, and the shortest label in the app is "App"
 * at ~31px. That still clears WCAG 2.5.8's 24x24 floor, but it is a real regression from
 * the ~63px the padding gave it, and short labels are the ones that need the help.
 *
 * A pseudo-element does not participate in layout, so it widens the target by 16px on
 * each side without moving the box, the label or the indicator. Adjacent tabs' targets
 * meet exactly in the middle of the 32px gap and never overlap.
 */
const HIT_TARGET_PADDING = 16

/**
 * The gap from a page-header tab row's rule down to the first content beneath it — 32px.
 *
 * ## Why this exists as a shared value
 *
 * Measured at 1280x800 during the VRG-470 conversion, the app had THREE different answers,
 * none of them chosen:
 *
 * | surface | rule -> first content | where it came from |
 * |---|---|---|
 * | Certificate detail (pre-existing underline) | 25px | the TabPanel's own `padding: 24px` |
 * | Billing (converted) | 34px | `StickyFormHeader`'s `pb: 4` + the TabPanel's `pt: 4` |
 * | Internal Analytics (converted) | **1px** | nothing at all — content butted against the rule |
 *
 * Each was an accident of whatever the consumer happened to wrap its panel in, which is the
 * per-page arrangement §29 records as having produced three different header gaps before the
 * gap was pulled into `Header`. Analytics at 1px is what the user reported.
 *
 * ## Why 32 and not a number that merely looks right
 *
 * 32px is already the app's "header block -> first content" value: working-conventions §40
 * fixes `description -> first content` at 32px on the app's `PageHeader`, and the shared
 * platform `Table` contributes its own 32px of leading above a toolbar — which `PeoplePage`'s
 * own comment calls "the value to match rather than the outlier to remove". Matching an
 * existing rhythm is defensible in a way that inventing a fourth value is not.
 *
 * ## Why a constant and NOT a `margin-bottom` on the Tabs root
 *
 * A blanket `mb` here would be the genuinely single-place fix, and it is wrong today: all ~25
 * pre-existing underline consumers already supply this gap themselves as panel padding (the
 * certificate page's 24px above), so a margin on the root would ADD to it and land them at
 * 49-57px. Making `Tabs` own the gap outright means first migrating every consumer off its own
 * panel padding — a larger, separate change. Until then this constant is the single source of
 * the VALUE, applied at each converted site.
 *
 * Converted pages are all on 32. The pre-existing underline consumers remain on their own
 * values (certificate detail 25px) — unifying them is the follow-up, not this ticket.
 */
export const TABS_CONTENT_GAP = 32

/**
 * TEMPORARY (VRG-470) — which of three tab-rule treatments to render, so all three can be
 * compared on one running preview instead of three deploys. **Remove once the user picks.**
 *
 * ## The problem being decided
 *
 * The rule under a tab row is doing TWO jobs at once:
 *   1. it is the tab TRACK — the 2px active indicator sits on it, so it belongs to the tabs; and
 *   2. it is the BOUNDARY between the header band and the page content.
 *
 * Those coincide happily until the row also carries right-side controls. Then the full-width
 * rule passes under the controls too and reads as THEIR underline — the whole row becomes one
 * strip. Observed on Studio, whose `Certificate`/`Mobile` menu buttons sit at the same baseline
 * as the tabs.
 *
 * | variant | rule | what it costs |
 * |---|---|---|
 * | `1` full-width (**default, committed**) | spans tabs + actions | conflates the two jobs |
 * | `2` tabs-width when actions exist | spans the labels only | keeps job 1, weakens job 2 — a wide page ends in a long unterminated right edge |
 * | `3` full-width, actions lifted off it | spans everything | keeps both, at the cost of a taller header band |
 *
 * ## Switching
 *
 * `?tabRule=2` on any URL, persisted to `localStorage` so it survives SPA navigation.
 * `?tabRule=1` restores the default; `localStorage.removeItem('vera:tabRule')` also does.
 *
 * Default is `1` so the committed branch renders exactly what was reviewed — a reader who has
 * never touched the flag sees the shipped behaviour.
 */
export type TabRuleVariant = 1 | 2 | 3

/**
 * Reads one flag from `?<param>=` (persisting it) or from `localStorage`, else the default.
 * Two axes share this so they switch INDEPENDENTLY — hold the rule constant and move the
 * indicator, or vice versa, without one resetting the other.
 */
const readFlag = <T extends string>(param: string, allowed: readonly T[], fallback: T): T => {
  if (typeof window === 'undefined') return fallback
  const key = `vera:${param}`
  try {
    const q = new URLSearchParams(window.location.search).get(param)
    if (q && (allowed as readonly string[]).includes(q)) {
      window.localStorage.setItem(key, q)
      return q as T
    }
    const stored = window.localStorage.getItem(key)
    if (stored && (allowed as readonly string[]).includes(stored)) return stored as T
  } catch {
    // Private mode / blocked storage — fall through to the shipped default rather than throw.
  }
  return fallback
}

/** Axis A. `?rule=1|2|3` (legacy alias `?tabRule=`, used in the first round of screenshots). */
export const tabRuleVariant = (): TabRuleVariant => {
  const legacy = readFlag('tabRule', ['1', '2', '3'] as const, '' as any)
  const v = readFlag('rule', ['1', '2', '3'] as const, (legacy || '1') as '1' | '2' | '3')
  return Number(v) as TabRuleVariant
}

/**
 * Axis B — the ACTIVE INDICATOR. `?ind=b1|b2|b3|b4`. **TEMPORARY (VRG-470); remove on decision.**
 *
 * | | indicator | active label |
 * |---|---|---|
 * | `b1` | the 90° purple gradient — the ORIGINAL, kept switchable for comparison | unchanged |
 * | `b2` **DEFAULT (chosen 2026-08-28)** | flat `brand[600]` — the theme token | unchanged |
 * | `b3` | neutral `text.primary` | unchanged |
 * | `b4` | neutral `text.primary` | `text.primary` + weight 600 |
 *
 * **b3 is included expecting it to lose**, because demonstrating the argument beats asserting
 * it. The active label is `rgb(109,109,109)` against inactive `rgb(98,98,98)` in light (contrast
 * **1.18**, and the active is the LIGHTER of the two) and **identical** in dark (**1.00**), at
 * weight 500 either way — so the indicator is the entire selection signal. Take its colour away
 * and there should be very little left, worst in dark.
 *
 * **b4 is the honest version of neutral**: it gives back the differentiated active label that
 * `SegmentedTabs` had (white pill + weight 600 + `text.primary`) and that converting to the
 * underline gave up. It is a PREVIEW ONLY — shipping that label change would alter all ~25
 * existing underline surfaces, which is a product decision, not this ticket's to take.
 */
export type TabIndicatorVariant = 'b1' | 'b2' | 'b3' | 'b4'

export const tabIndicatorVariant = (): TabIndicatorVariant =>
  readFlag('ind', ['b1', 'b2', 'b3', 'b4'] as const, 'b2')

/**
 * How far variant 3 lifts right-side controls above the rule, in px.
 *
 * 16 rather than a token: the association to break is a VISUAL adjacency, and what matters is
 * that the gap below the control clearly exceeds the ~6px that made Studio's buttons look
 * seated on the rule. Provisional — it exists to be looked at, not to be canonised.
 */
export const TAB_RULE_LIFT = 16

/**
 * Axis C — WHERE Studio's preview dropdowns live. `?preview=1|2`. **TEMPORARY (VRG-470).**
 *
 * | | `Certificate ▾` / `Mobile ▾` |
 * |---|---|
 * | `1` **default, committed** | right of the tab row, absolutely positioned over it at `top: 6` |
 * | `2` | in the preview pane's own control band, beside the zoom stepper |
 *
 * The argument for `2`: those dropdowns choose what the preview RENDERS and at what device —
 * a viewport parameter — whereas the `Certificate`/`Email` tabs beside them choose which
 * template you are EDITING. Two different classes of control sharing one row. The preview pane
 * already owns a control of the first class (the zoom stepper), so that is where they belong,
 * and the fact that they had to be ABSOLUTELY POSITIONED into the tab row is the evidence they
 * never had a home in it.
 *
 * Consequence worth noting for axis A: under `2`, Studio's tab row has no right-side controls
 * at all, so the full-width rule (`rule=1`) stops running under anything and option `rule=2`
 * may not be needed.
 */
export type PreviewControlsPlacement = 1 | 2

export const previewControlsPlacement = (): PreviewControlsPlacement =>
  Number(readFlag('preview', ['1', '2'] as const, '1')) as PreviewControlsPlacement

const tabHitTargetSx = {
  position: 'relative',
  // MUI's Tab sets `overflow: hidden` to clip the touch ripple, which also clips this
  // pseudo-element back to the label's own box — the extension computes correctly and
  // then never hit-tests (verified with `elementFromPoint`, which returned the flex
  // container). Unsetting it is safe HERE and only here: these tabs pass `disableRipple`,
  // so there is no ripple left for the clip to contain.
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `-${HIT_TARGET_PADDING}px`,
    right: `-${HIT_TARGET_PADDING}px`,
  },
} as const
interface TabsProps {
  tabs: tabsType,
  children?: React.ReactNode,
  redirect?: boolean,
  sx?: SxProps,
  /** Controlled active value. When provided, the component is controlled. */
  value?: string,
  /** Called with the new value when a tab is selected (controlled mode). */
  onChange?: (value: string) => void,
  /**
   * Trailing content rendered right-aligned on the SAME line as the tab list, with the
   * rule running underneath both. For a page action that only applies to the active tab.
   *
   * Mirrors `SegmentedTabs`' prop of the same name deliberately — VRG-470 converts the
   * page-header tab rows from that component to this one, and two of them (the internal
   * Company detail tabs and the Signups waitlist/allowlist tabs) carry a per-tab action
   * level with the tabs. Without this they would each hand-roll a flex wrapper, which is
   * the drift §36 exists to stop; the pair of components should present the same surface
   * where they do the same job.
   *
   * Additive: when it is not passed the markup is byte-identical to before, so none of
   * the pre-existing underline consumers are affected.
   */
  rightComponents?: React.ReactNode,
}

type tabsType = {
  label: string
  value: string, 
  component?: React.ReactNode
}[]

export const Tabs = (props: TabsProps) => {
  const { tabs, sx, children, redirect = true, value: valueProp, onChange, rightComponents } = props
  const theme = useTheme();
  const location = useLocation();

  /**
   * The tab this pathname selects, or `null` when it selects none.
   *
   * `redirect` mode keeps the pathname verbatim because the tab VALUES are route
   * paths there and the TabPanels are keyed by them; `redirect={false}` maps back
   * to the matched tab's own value, which may be a route PREFIX (`/tags` for
   * `/tags/shop/cart`) or not a route at all.
   *
   * NOTE this is a SUBSTRING test, so it only works while a tab's value is literally
   * present in the URLs it owns. Settings used to rely on that (`/settings/company`
   * ⊂ `/settings/company/general`) and no longer can — VRG-221 flattened its leaves
   * to `/settings/general`, which contains neither tab value. SettingsPage therefore
   * passes `value` and is CONTROLLED; the scope comes from which menu holds the
   * matched section. Any other caller whose paths stop containing their tab value
   * has to do the same, because the no-match branch silently falls back to `tabs[0]`.
   */
  const tabForPathname = (pathname?: string): string | null => {
    if (!pathname) return null;
    const matchingTab = tabs.find((tab) => pathname.includes(tab.value));
    if (!matchingTab) return null;
    return redirect ? pathname : matchingTab.value;
  };

  const [internalValue, setInternalValue] = useState(() => {
    if (!location?.pathname) return tabs[0]?.value || '';
    // `redirect` mode keeps the raw pathname even when it matches no tab — unchanged
    // from before, so a page whose URL is a superset of its tab values renders exactly
    // as it always has on the first paint.
    return tabForPathname(location.pathname) ?? (redirect ? location.pathname : tabs[0]?.value || '');
  });

  const isControlled = valueProp !== undefined;

  // Follow the URL for the whole life of the component, not just the first render.
  //
  // The value used to be derived in the `useState` initializer alone, so an
  // uncontrolled Tabs froze on whatever the pathname was at MOUNT. Every later route
  // change — an in-page link, a `navigate()`, the browser Back button — moved the URL
  // while TabContext kept rendering the old panel, and the two only agreed again if
  // something happened to unmount the page. Reproduced two ways before this fix:
  //   - Settings (redirect={false}, tab bar hidden, so `handleChange` can never fire):
  //     clicking any Company row went to the company section with the Personal panel
  //     still on screen. (Settings is now controlled — see `tabForPathname` above —
  //     so it no longer depends on this path, but the failure it describes is real
  //     for any uncontrolled caller.)
  //   - Tags (redirect, uncontrolled): Back from /tags/shop to /tags/inventory left the
  //     Shop panel and the Shop underline in place.
  //
  // Assigned during render against a pathname ref rather than in an effect: an effect
  // would paint one frame of the WRONG panel first. Guarded on an actual tab match, so
  // a pathname that selects no tab leaves the current selection alone instead of
  // snapping back to the first tab — this only ever un-freezes the value, it never
  // resets one.
  const lastPathnameRef = useRef(location?.pathname);
  if (lastPathnameRef.current !== location?.pathname) {
    lastPathnameRef.current = location?.pathname;
    if (!isControlled) {
      const next = tabForPathname(location?.pathname);
      if (next !== null && next !== internalValue) setInternalValue(next);
    }
  }

  const value = isControlled ? valueProp : internalValue;

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (!isControlled) setInternalValue(newValue);
    onChange?.(newValue);
  };

  // TEMPORARY (VRG-470) — see `tabRuleVariant`. Read per render so a `?tabRule=` switch applies
  // on the next paint without a rebuild.
  const ruleVariant = tabRuleVariant()
  // Variant 2 moves the rule off the full-width TabList onto `.MuiTabs-flexContainer`, which is
  // exactly the width of the labels (measured 148.2px for a two-tab row against the TabList's
  // 975). Applied whether or not `rightComponents` is passed, because a page can put controls on
  // the row WITHOUT the prop — Studio absolutely-positions its two menu buttons over the row, and
  // that is the surface this whole question came from.
  const variant2Sx =
    ruleVariant === 2
      ? { borderBottom: 'none', '& .MuiTabs-flexContainer': { borderBottom: `1px solid` , borderColor: 'divider' } }
      : {}

  // Axis B. Overrides `StyledMuiTabList`'s hardcoded gradient at render time, because the styled
  // component is built once at module scope and cannot read a per-render flag.
  const indVariant = tabIndicatorVariant()
  const indicatorSx =
    indVariant === 'b1'
      ? {}
      : {
          '& .MuiTabs-indicator': {
            background: 'none',
            backgroundColor: (theme: any) =>
              indVariant === 'b2'
                ? theme.palette.brand?.[600] || '#7F48DF'
                : theme.palette.text.primary,
          },
          // b4 only: give the SELECTED label its own weight + colour, which is the thing the
          // underline currently does not do at all (see `tabIndicatorVariant`).
          ...(indVariant === 'b4'
            ? {
                '& .MuiTab-root.Mui-selected': {
                  color: (theme: any) => theme.palette.text.primary,
                  fontWeight: 600,
                },
              }
            : {}),
        }

  const tabList = (
        <StyledMuiTabList
          sx={{
            paddingRight: 0,
            ...variant2Sx,
            ...indicatorSx,
            // With `rightComponents` the rule moves to the ROW so it runs under the actions
            // too; leaving it here as well would draw two rules the same 1px apart, which
            // reads as a rendering fault rather than as a border (the same trap
            // `StickyFormHeader`'s `stuckEdge` prop documents).
            //
            // `sx` follows the rule to the ROW for the same reason: with `rightComponents` the
            // row IS the tab row, so a caller's `mb: TABS_CONTENT_GAP` has to sit below the
            // rule. Applied here it would open a gap INSIDE the row — under the tabs but above
            // their own underline — which is not what any caller means by it. No pre-existing
            // consumer passes `rightComponents` (it is new in VRG-470), so this split costs
            // nothing in back-compat.
            ...(rightComponents ? { borderBottom: 'none' } : sx),
          }}
          onChange={handleChange}
          textColor="primary"
          indicatorColor="secondary"
          variant="scrollable"
          scrollButtons={false}
          aria-label=""
        >
          {tabs.map((tab) => {
            const tabProps = redirect && location ? {
              component: Link,
              to: tab.value,
            } : {};

            return (
            <Tab
              key={`tab-${tab.label}`}
              disableRipple
              // No HORIZONTAL padding: the box must equal the label so the indicator
              // underlines the label and the first label lands on the content datum
              // (see StyledMuiTabList above). Spacing between tabs comes from the
              // container's `gap`. Vertical padding stays — it sets the row's height.
              sx={{
                px: 0,
                py: 4,
                fontSize: '16px',
                textTransform: 'none',
                minWidth: 0,
                ...tabHitTargetSx,
              }}
              {...tabProps}
              value={tab.value}
              label={tab.label}
            />)
          })}
        </StyledMuiTabList>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <TabContext value={value}>
        {rightComponents ? (
          <Box
            // ARRAY form, not `{...base, ...sx}`. `SxProps` may itself be a function or an
            // array, so spreading it into an object literal is not merely untidy — it does
            // not typecheck (TS2769: the spread widens the callback's return past
            // `SystemStyleObject`). The array form is MUI's supported way to compose a
            // component's own sx with a caller's.
            sx={[
              (theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                // The rule spans the whole row, under the tabs AND the actions, so the tab
                // row still reads as one full-width band rather than an underlined fragment
                // with a button floating beside it.
                //
                // VARIANT 2 suppresses it here — the rule moves onto `.MuiTabs-flexContainer`
                // (the tab labels' own width) via `variant2Sx`, so nothing is drawn under the
                // actions. Leaving this on would defeat the whole variant.
                ...(ruleVariant === 2
                  ? {}
                  : { borderBottom: `1px solid ${theme.palette.divider}` }),
                // Variant 3 lifts the actions rather than the rule, so the row must stop
                // stretching them to full height.
                ...(ruleVariant === 3 ? { alignItems: 'flex-end' } : {}),
              }),
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
          >
            {tabList}
            {/* `flexShrink: 0` so a long tab set scrolls (the TabList is `variant="scrollable"`)
                instead of squeezing the action to nothing.

                VARIANT 3 lifts this box off the rule: `alignItems: flex-start` on the row plus a
                bottom margin, so the control's bottom edge clears the rule by TAB_RULE_LIFT
                instead of sitting ~6px above it. The rule stays full-width (job 2 intact); what
                changes is that it is no longer visually adjacent to the control, so it stops
                reading as the control's underline. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                ...(ruleVariant === 3 ? { alignSelf: 'flex-start', mb: `${TAB_RULE_LIFT}px` } : {}),
              }}
            >
              {rightComponents}
            </Box>
          </Box>
        ) : (
          tabList
        )}
      {children}
      </TabContext>
    </Box>
  );
}

// Export TabPanel from @mui/lab for use with Tabs component
export { TabPanel } from '@mui/lab';

